import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableHead, TableRow, TableCell, TableBody } from '@trackman/web-shared-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import Paragraph from 'components/TextComps/Paragraph';
import { useServices, useStores } from 'index';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';
import { sortByString } from 'utilities/dataSorters';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { getErrorMessage } from 'utilities/ErrorHelper';

export const PartnersList = () => {
  useSetPageTitle({ title: 'Partners List', subtitle: '' });
  const queryClient = useQueryClient();
  const partnersService = useServices().partnersService;
  const notificationsStore = useStores().notificationStore;
  const partnersEditorService = useServices().partnersEditorService;

  const { data: partnersList, isPending } = useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      const response = await partnersService.getPartners();
      return sortByString(response, 'fullName', 'ASCENDING');
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const [isModalOn, setIsModalOn] = useState(false);
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');
  const [searchValue, setSearchValue] = useState('');
  const [partnerToDelete, setPartnerToDelete] = useState<string | null>(null);

  const partnersListToDisplay = partnersList
    ? sortByString(
        partnersList.filter((partner) => partner.fullName.toLowerCase().includes(searchValue.toLowerCase())),
        'fullName',
        sortOrder
      )
    : [];

  const deletePartner = useMutation({
    mutationFn: () => partnersEditorService.deletePartner(partnerToDelete ?? ''),
    onSuccess: async () => await Promise.all([queryClient.invalidateQueries({ queryKey: ['partners'] })]),
    onError: (ex) => notificationsStore.addToast('error', getErrorMessage(ex), 'error'),
    onSettled: () => {
      setPartnerToDelete(null);
      setIsModalOn(false);
    },
  });

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderSearchbar searchValue={searchValue} onChange={setSearchValue} />
        <HeaderCreateButton buttonTitle='New partner' route={DefaultRoutes.PartnersEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>
                Name
                <SortOrderButton isActive={true} sortOrder={sortOrder} onSortChange={(sortOrder) => setSortOrder(sortOrder)} />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partnersListToDisplay.length && !isPending ? (
              partnersListToDisplay.map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.PartnersEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className={`row-button ${isPending ? 'disabled' : ''}`}
                          onClick={() => {
                            setPartnerToDelete(row.id);
                            setIsModalOn(true);
                          }}
                          disabled={isPending}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No partners found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal
        isModalOn={isModalOn}
        setIsModalOn={setIsModalOn}
        deleteAction={() => deletePartner.mutate()}
        itemName='partner'
      />
    </div>
  );
};
